/**
 * One-time self-heal when the page's own code or styles fail to load (#917).
 *
 * During a rolling deploy, old and new pods serve side by side. A page can get
 * `index.html` from a new pod and then ask an old pod for a hashed bundle it
 * does not have (404): the portal renders unstyled, or blank if the entry
 * script is the casualty. Reloading once almost always lands on a consistent
 * pod set.
 *
 * "Once" is enforced with a timestamp in `sessionStorage`: a second failure
 * within {@link RELOAD_GUARD_WINDOW_MS} of the last automatic reload leaves
 * the page alone, so a genuinely broken deploy cannot reload in a loop, while
 * a failure during a later deploy (or in a new tab) still self-heals.
 *
 * Keep this module dependency-free: it is also compiled on its own into the
 * tiny classic script that `vite.config.ts` puts first in `<head>`
 * (`src/boot/selfHeal.ts`), so its listener exists before the stylesheet
 * starts loading.
 */

/** Guard key for asset/chunk load failures. The value is the reload's epoch ms. */
export const SELF_HEAL_RELOAD_KEY = "bondit:self-heal-reload";

/** A second automatic reload within this window is suppressed. */
export const RELOAD_GUARD_WINDOW_MS = 10 * 60 * 1000;

interface ReloadOnceOptions {
  windowMs?: number;
  win?: Window;
  now?: () => number;
}

/**
 * Reloads the page unless this tab already did so for `key` within the guard
 * window. Returns whether a reload was started. Without a working
 * `sessionStorage` there is no loop protection, so it never reloads.
 */
export const reloadOnce = (
  key: string,
  { windowMs = RELOAD_GUARD_WINDOW_MS, win = window, now = Date.now }: ReloadOnceOptions = {}
): boolean => {
  const at = now();
  try {
    const storage = win.sessionStorage;
    const last = Number(storage.getItem(key));
    // `at < last` means the clock moved backwards: treat the stamp as stale.
    if (last > 0 && at >= last && at - last < windowMs) {
      return false;
    }
    storage.setItem(key, String(at));
  } catch {
    return false;
  }
  win.location.reload();
  return true;
};

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Unable to preload CSS/i, // Vite's preload helper
];

/** A failed `import()` of one of our chunks, e.g. a rejected lazy route. */
export const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  return (
    error.name === "ChunkLoadError" ||
    CHUNK_ERROR_PATTERNS.some(pattern => pattern.test(error.message))
  );
};

/** Only our own hashed build output counts: never analytics or third parties. */
const isOwnAsset = (url: string, location: Location): boolean => {
  try {
    const parsed = new URL(url, location.href);
    return parsed.origin === location.origin && parsed.pathname.startsWith("/assets/");
  } catch {
    return false;
  }
};

/**
 * The URL of a stylesheet `<link>` or `<script>` whose load failed, or null for
 * any other element (images, preloads, iframes) — those never trigger a reload.
 */
const failedAssetUrl = (target: EventTarget | null): string | null => {
  if (!target || typeof (target as Element).tagName !== "string") {
    return null;
  }
  const element = target as Element;
  if (element.tagName === "LINK") {
    const rel = (element.getAttribute("rel") ?? "").toLowerCase().split(/\s+/);
    return rel.includes("stylesheet") ? (element as HTMLLinkElement).href : null;
  }
  if (element.tagName === "SCRIPT") {
    return (element as HTMLScriptElement).src || null;
  }
  return null;
};

const INSTALLED_FLAG = "__bonditSelfHealInstalled";

type FlaggedWindow = Window & { [INSTALLED_FLAG]?: boolean };

/**
 * Registers the self-heal listeners once per page (idempotent: the boot script
 * and `main.tsx` both call it; whichever runs first wins).
 *
 * - `error` in the capture phase on `window`: load errors do not bubble, but
 *   capture listeners on `window` still see them. Fires for a failed
 *   `<link rel="stylesheet">` or `<script>` under our own `/assets/`.
 * - `vite:preloadError`: Vite's helper for `import()` failed to load a chunk
 *   or its CSS. `preventDefault()` only when reloading; otherwise the original
 *   error propagates to the caller and the route error boundary.
 */
export const installSelfHeal = (win: Window = window): void => {
  const flagged = win as FlaggedWindow;
  if (flagged[INSTALLED_FLAG]) {
    return;
  }
  flagged[INSTALLED_FLAG] = true;

  win.addEventListener(
    "error",
    event => {
      const url = failedAssetUrl(event.target);
      if (url !== null && isOwnAsset(url, win.location)) {
        reloadOnce(SELF_HEAL_RELOAD_KEY, { win });
      }
    },
    true
  );

  win.addEventListener("vite:preloadError", event => {
    if (reloadOnce(SELF_HEAL_RELOAD_KEY, { win })) {
      event.preventDefault();
    }
  });
};
